<template>
  <aw-spring :desktop-size="10">
    <nav
      v-if="isMounted"
      :class="[$style.categoryHeaderNav, {[$style.categoryHeaderNavHasTooltip]: hasTooltip}]"
      :style="{overflowX: showTooltip ? '' : 'auto' , '--tab-count': tabCount, '--tablet-tab-count': tabCount-1 }"
    >
      <div>
        <button
          aria-hidden="true"
          tabindex="-1"
          type="button"
          :class="[$style.tab, $style.tabMain]"
          @click="$emit('open-online-store-menu')"
          v-text="$awt('aw.category_header.all_categories_btn')"
        />
        <button
          tabindex="0"
          :class="['awSrOnly']"
          @click="$emit('open-online-store-menu-enter')"
        >
          {{ $awt('aw.dh_header.menu_block.menu_btn.text') }}
        </button>
      </div>
      <template v-for="(tab, i) in tabCategories">
        <component
          :is="openCategory(tab, true).is"
          v-if="tab.isCategory"
          :key="`horizontal-menu-items-${tab.id}`"
          v-bind="openCategory(tab, true).attrs"
          :class="$style.tab"
        >
          {{ tab.name }}
        </component>
        <lv-link
          v-else-if="tab.isFavorite"
          :key="`horizontal-menu-items-favourite-${i}`"
          :to="localePath('/shop/profile/saved-baskets')"
          :class="$style.tab"
        >
          {{ $awt('aw.category_header.favourite') }}
        </lv-link>
      </template>
      <lv-tooltip
        v-show="hasTooltip"
        :key="`tooltip-secondary-version2`"
        v-model:show-tooltip="showTooltip"
        trigger="click"
        styling="secondary-version2"
        :options="{
          placement: 'bottom-end',
          modifiers: {
            offset: {
              offset: '0,10'
            }
          }
        }"
      >
        <template #tooltip>
          <div data-popover-item-wrapper :class="$style.tooltipList">
            <component
              :is="openCategory(tab, true).is"
              v-for="tab in tooltipCategories"
              :key="`tooltip-menu-items-${tab.id}`"
              v-bind="openCategory(tab, true).attrs"
              data-popover-item
              :data-popover-item-selected="isActiveCategory(tab)"
            >
              {{ tab.name }}
            </component>
          </div>
        </template>
        <template #reference>
          <span :class="[$style.tab]" @click="showTooltip = !showTooltip">
            {{ $awt('aw.category_header.show_more') }}
          </span>
        </template>
      </lv-tooltip>
    </nav>
  </aw-spring>
</template>

<script>
  import { resolveComponent } from 'vue';
  import { mapState } from 'pinia';
  import { useRoute } from 'nuxt/app';
  import { LvLink, LvIcon, LvGrid, LvButton, LvTooltip } from '~~/common/components/loginet-vue-shop/index.mjs';
  import urlMixin from '~~/common/mixins/urlMixin';

  import {
    BOUTIQUE_FLAG_PRODUCTS,
    BOUTIQUE_SHORTLIST,
    BOUTIQUE_REDIRECT_URL,
  } from '~~/common/config/BoutiqueConfig.js';
  import pageInstanceMixin from '~~/common/mixins/pageInstanceMixin';
  import AwSpring from '~~/common/components/AwSpring';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useUserStore } from '~~/common/stores/user';

  const TAB_ITEM_COUNT = 4;
  const TAB_MODE_MIN_SIZE = 'desktop-small-min';
  export default {
    name: 'AwCategoryHeader',
    components: {
      LvTooltip,
      AwSpring,
      LvLink,
      LvButton,
      LvIcon,
      LvGrid,
    },
    mixins: [
      urlMixin,
      pageInstanceMixin,
    ],
    emits: [
      'open-online-store-menu',
      'open-online-store-menu-enter',
    ],
    data () {
      return {
        isMounted: false,
        allCategoriesHoverId: null,
        subCategoryOpenedIndex: null,
        menuScrollTimer: null,
        showTooltip: false,
        hyperLinkSettings: {
          base: (category, directOpen) => ({
            is: directOpen ? resolveComponent('lv-link') : 'a',
            attrs: {
              [directOpen ? 'to' : 'href']: directOpen ? this.getCategoryUrl(category) : 'javascript: void(0)',
            },
          }),
          [BOUTIQUE_FLAG_PRODUCTS]: category => ({
            is: resolveComponent('lv-link'),
            attrs: {
              to: this.getBoutiqueUrl(category),
            },
          }),
          [BOUTIQUE_SHORTLIST]: ({
            boutiqueContentRef,
            id,
          }) => ({
            is: resolveComponent('lv-link'),
            attrs: {
              to: this.getShortlistUrl({
                identifier: boutiqueContentRef,
                shType: 'id',
                boutiqueId: id,
                categoryId: id,
              }),
            },
          }),
          [BOUTIQUE_REDIRECT_URL]: ({ boutiqueContentRef }) => {
            const {
              isOuter,
              link,
            } = this.$urlParts(boutiqueContentRef);
            return {
              is: isOuter ? 'a' : resolveComponent('lv-link'),
              attrs: {
                [isOuter ? 'href' : 'to']: `${isOuter ? '' : this.localeCodeToUrl}${link}`,
                target: isOuter ? '_blank' : null,
              },
            };
          },
        },
      };
    },
    computed: {
      ...mapState(useUserStore, {
        isEcomUser: state => state.isEcomUser,
      }),
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useCategoryStore, {
        categories: state => (state.categories?.children || []),
      }),
      hasTooltip () {
        return this.tooltipCategories.length > 0;
      },
      filteredCategories () {
        const categories = this.categories.filter(category => category.boutique).map(category => ({
          ...category,
          isCategory: true,
        }));
        if (this.isEcomUser && this.isShop) {
          categories.splice(3, 0, {
            isFavorite: true,
          });
        }
        return categories;
      },
      tabCategories () {
        return this.screenRange[TAB_MODE_MIN_SIZE] ? this.filteredCategories.slice(0, TAB_ITEM_COUNT) : this.filteredCategories;
      },
      tooltipCategories () {
        return this.screenRange[TAB_MODE_MIN_SIZE] ? this.filteredCategories.slice(TAB_ITEM_COUNT, 100) : [];
      },
      tabCount () {
        if (this.screenRange[TAB_MODE_MIN_SIZE]) {
          return this.tabCategories.length > TAB_ITEM_COUNT ? TAB_ITEM_COUNT : this.tabCategories.length + 1;
        } else {
          return this.tabCategories.length;
        }
      },
    },
    mounted () {
      this.isMounted = true;
    },
    methods: {
      isActiveCategory (category) {
        const route = useRoute();
        return (category.boutique ? this.getBoutiqueUrl(category) : this.getCategoryUrl(category)) === route.fullPath;
      },
      openCategory (category, directOpen) {
        return this.hyperLinkSettings[category.boutique ? category.boutiqueContentType : 'base'](category, directOpen);
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">

$box-distance: 16px;

.categoryHeaderNav {
  font-family: $secondary-font;
  font-size: 14px;
  font-weight: $font-weight-medium-v2;
  line-height: 20px;
  position: relative;
  z-index: 1;

  display: grid;
  width: 100%;
  margin-top: 40px;
  gap: 16px;
  grid-template-columns: auto repeat(var(--tab-count, 6), 1fr);

  scrollbar-width: none; /* Firefox */

  @include mobile(max) {
    //100% + lv-spring right margin
    width: calc(100% + 16px);
    margin-top: 20px;
  }

  @include tablet(min) {
    grid-template-columns: max-content repeat(var(--tab-count, 6), max-content);
  }

  @include desktop-small(min) {
    grid-template-columns: 1fr repeat(var(--tab-count, 6), 1fr);
  }

  @include desktop-medium(min) {
    grid-template-columns: repeat(var(--tab-count, 10), 1fr);
  }

  &HasTooltip {
    @include desktop-small(min) {
      grid-template-columns: 1fr repeat(var(--tablet-tab-count, 5), 1fr) auto;
    }
    @include desktop-medium(min) {
      grid-template-columns: repeat(var(--tab-count, 10), 1fr) auto;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  .tab {
    font-size: 12px;

    font-weight: $font-weight-medium-v2;
    line-height: 16px;
    display: block;

    height: 100%;
    padding: 14px 20px;
    text-align: center;
    white-space: nowrap;
    color: $color-text-primary;
    border: none;
    border-radius: 12px;
    background: $color-white;

    &Main {
      width: 100%;
      color: $color-white;
      background: $color-brand-primary;

      &:hover,
      &:active,
      &:focus {
        background: $color-brand-primary-active;
      }
    }
  }
}

.tooltip {
  &List {
    display: flex;
    flex-direction: column;
  }
}
</style>
